import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Flex, Heading, Icon, Select, Text } from "@telia/teddy";
import { API_URL_FEEDBACK_MESSAGES, API_URL_SUPERADMIN_FEEDBACK } from "../../api";
import { retailerFetch } from "../../retailerFetch";
import { getSalesAgent } from "../../state/selectors/selectors";
import { type FeedbackDetailsResponse, type FeedbackMessage, type FeedbackStatus } from "../SuperAdmin/feedbackTypes";
import { type RootState } from "../../../../../models";
import PAGES from "../../../../../constants/pages";

const STATUS_LABELS: Record<FeedbackStatus, string> = {
    open: "Åpen",
    in_progress: "Under behandling",
    resolved: "Løst",
    ignored: "Ignorert",
};

type ChatItem = { type: "message"; msg: FeedbackMessage } | { type: "resolved"; timestamp: string };
import "./FeedbackDetails.less";

const formatDateTime = (iso: string) =>
    new Date(iso).toLocaleString("nb-NO", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });

const FeedbackDetails: React.FC = () => {
    const { feedbackId } = useParams<{ feedbackId: string }>();
    const salesAgent = useSelector((state: RootState) => getSalesAgent(state));
    const [feedback, setFeedback] = useState<FeedbackDetailsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [newMessage, setNewMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const chatEndRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    const fetchMessages = async () => {
        try {
            const res = await retailerFetch(`${API_URL_FEEDBACK_MESSAGES}/${feedbackId}/messages`);
            if (res.status === 403) {
                setError("Du har ikke tilgang til denne tilbakemeldingen.");
                return;
            }
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data: FeedbackDetailsResponse = await res.json();
            setFeedback(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Kunne ikke hente tilbakemelding");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMessages();
    }, [feedbackId]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [feedback?.messages]);

    const handleSend = async () => {
        if (!newMessage.trim() || sending) return;
        setSending(true);
        try {
            const res = await retailerFetch(`${API_URL_FEEDBACK_MESSAGES}/${feedbackId}/messages`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ message: newMessage.trim() }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const created: FeedbackMessage = await res.json();
            setFeedback((prev) => (prev ? { ...prev, messages: [...prev.messages, created] } : prev));
            setNewMessage("");
        } catch (err) {
            setError(err instanceof Error ? err.message : "Kunne ikke sende melding");
        } finally {
            setSending(false);
        }
    };

    const handleStatusUpdate = async (newStatus: FeedbackStatus) => {
        if (updatingStatus || !feedback || newStatus === feedback.feedback.status) return;
        setUpdatingStatus(true);
        try {
            const res = await retailerFetch(`${API_URL_SUPERADMIN_FEEDBACK}/${feedback.feedback.id}/status`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ status: newStatus }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            await fetchMessages();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Kunne ikke oppdatere status");
        } finally {
            setUpdatingStatus(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const buildChatItems = (msgs: FeedbackMessage[], resolvedAt: string | null): ChatItem[] => {
        const items: ChatItem[] = msgs.map((msg) => ({ type: "message", msg }));
        if (resolvedAt) {
            const resolvedTime = new Date(resolvedAt).getTime();
            const insertIdx = items.findIndex(
                (item) => item.type === "message" && new Date(item.msg.createdAt).getTime() > resolvedTime,
            );
            const resolvedItem: ChatItem = { type: "resolved", timestamp: resolvedAt };
            if (insertIdx === -1) {
                items.push(resolvedItem);
            } else {
                items.splice(insertIdx, 0, resolvedItem);
            }
        }
        return items;
    };

    if (loading) {
        return (
            <Box className="feedback-details">
                <Text>Laster...</Text>
            </Box>
        );
    }

    if (error) {
        return (
            <Box className="feedback-details">
                <Text className="feedback-details__error">{error}</Text>
            </Box>
        );
    }

    if (!feedback) return null;

    const { feedback: entry, messages } = feedback;
    const isCreator = String(salesAgent?.id) === entry.agentId;
    const chatItems = buildChatItems(messages, entry.resolvedAt);

    const backUrl = isCreator ? PAGES.RETAILER.FEEDBACKS.URL : `${PAGES.RETAILER.FEEDBACKS.URL}${entry.agentId}`;
    const backLabel = isCreator
        ? "Se alle dine tilbakemeldinger"
        : `Se alle ${entry.agentName ?? "agent"} sine tilbakemeldinger`;

    return (
        <Box className="feedback-details">
            <Flex
                className="feedback-details__back-btn"
                align="center"
                gap="050"
                role="button"
                tabIndex={0}
                onClick={() => navigate(backUrl)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(backUrl);
                    }
                }}
            >
                <Icon name="arrow-left" size="sm" aria-hidden="true" focusable="false" />
                <Text>{backLabel}</Text>
            </Flex>

            <Flex justify="space-between" align="center" mt="200" mb="200">
                <Heading mb="100" as="h1" variant="title-300" className="feedback-details__title">
                    {entry.feedbackType}
                </Heading>
                {!isCreator && (
                    <Flex className="feedback-details__status-area" align="center" gap="200">
                        <Select
                            name="feedbackStatus"
                            label="Status"
                            options={Object.keys(STATUS_LABELS)}
                            value={entry.status}
                            onValueChange={(val) => {
                                handleStatusUpdate(val as FeedbackStatus);
                                return val;
                            }}
                            disabled={updatingStatus}
                        >
                            <Select.Trigger className="feedback-details__status-select">
                                <Select.Value placeholder="Velg status" />
                            </Select.Trigger>
                            <Select.Content>
                                <Select.Viewport>
                                    {Object.entries(STATUS_LABELS).map(([value, label]) => (
                                        <Select.Item key={value} value={value}>
                                            {label}
                                        </Select.Item>
                                    ))}
                                </Select.Viewport>
                            </Select.Content>
                        </Select>
                    </Flex>
                )}
            </Flex>

            <Box className="feedback-details__chat">
                {/* Initial description as the first bubble from the feedback creator */}
                {entry.description && (
                    <Flex className="feedback-details__bubble-row feedback-details__bubble-row--right">
                        <Box className="feedback-details__bubble feedback-details__bubble--creator">
                            <Text className="feedback-details__bubble-text">{entry.description}</Text>
                            <Text className="feedback-details__bubble-meta">
                                {!isCreator && <>{entry.agentName ?? "Avsender"} &middot; </>}
                                {formatDateTime(entry.createdAt)}
                            </Text>
                        </Box>
                    </Flex>
                )}

                {chatItems.map((item) =>
                    item.type === "resolved" ? (
                        <Flex
                            key="resolved"
                            className="feedback-details__bubble-row feedback-details__bubble-row--left"
                        >
                            <Box className="feedback-details__bubble feedback-details__bubble--resolved">
                                <Flex align="center" gap="100">
                                    <Icon name="check-circle-filled" size="md" aria-hidden="true" focusable="false" />
                                    <Text className="feedback-details__bubble-text feedback-details__resolved-text">
                                        Tilbakemeldingen har blitt merket som løst
                                    </Text>
                                </Flex>
                                <Text className="feedback-details__bubble-meta">{formatDateTime(item.timestamp)}</Text>
                            </Box>
                        </Flex>
                    ) : (
                        <Flex
                            key={item.msg.id}
                            className={`feedback-details__bubble-row feedback-details__bubble-row--${item.msg.senderId === entry.agentId ? "right" : "left"}`}
                        >
                            <Box
                                className={`feedback-details__bubble feedback-details__bubble--${item.msg.senderId === entry.agentId ? "creator" : "admin"}`}
                            >
                                <Text className="feedback-details__bubble-text">{item.msg.message}</Text>
                                <Text className="feedback-details__bubble-meta">
                                    {!(isCreator && item.msg.senderId === entry.agentId) && (
                                        <>
                                            {item.msg.senderName.split(" ")[0]}
                                            {item.msg.senderId !== entry.agentId ? " (Nexus-teamet)" : ""} &middot;{" "}
                                        </>
                                    )}
                                    {formatDateTime(item.msg.createdAt)}
                                </Text>
                            </Box>
                        </Flex>
                    ),
                )}

                {isCreator && !messages.some((msg) => msg.senderId !== entry.agentId) && !entry.resolvedAt && (
                    <Flex className="feedback-details__no-reply-notice" gap="100" align="center" justify="center">
                        <Icon name="info" size="sm" aria-hidden="true" focusable="false" />
                        <Text variant="additional-100-medium">
                            Vi vurderer tilbakemeldinger fortløpende og sender deg en varsling når din er gjennomgått.
                        </Text>
                    </Flex>
                )}

                <div ref={chatEndRef} />
            </Box>

            <Box className="feedback-details__input-area">
                <textarea
                    className="feedback-details__textarea"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Skriv en melding..."
                    maxLength={1000}
                    rows={2}
                />
                <Button
                    variant="primary"
                    onClick={handleSend}
                    disabled={!newMessage.trim() || sending}
                    className="feedback-details__send-btn"
                >
                    {sending ? "Sender..." : "Send"}
                </Button>
            </Box>
        </Box>
    );
};

export default FeedbackDetails;
